import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { Dialog, DialogContent } from './ui/dialog';
import { X } from 'lucide-react';

const ImagePreviewContext = createContext({ open: () => {} });

/**
 * Provides a global lightbox for image previews. Wrap the app with
 * <ImagePreviewProvider> and call useImagePreview().open(url) from anywhere.
 */
export function ImagePreviewProvider({ children }) {
  const [src, setSrc] = useState(null);
  const [caption, setCaption] = useState('');

  const open = useCallback((url, label = '') => {
    if (!url) return;
    setSrc(url);
    setCaption(label);
  }, []);

  const close = () => { setSrc(null); setCaption(''); };

  return (
    <ImagePreviewContext.Provider value={{ open }}>
      {children}
      <Dialog open={!!src} onOpenChange={(o) => !o && close()}>
        <DialogContent
          className="max-w-4xl w-[95vw] bg-black/95 border border-white/10 p-2"
          data-testid="image-preview-dialog"
        >
          <button
            onClick={close}
            className="absolute top-3 right-3 z-10 p-2 rounded-full bg-black/60 hover:bg-black/80 text-white"
            aria-label="Close preview"
            data-testid="image-preview-close-btn"
          >
            <X className="w-5 h-5" />
          </button>
          {src && (
            <img
              src={src}
              alt={caption || 'Preview'}
              className="w-full h-auto max-h-[85vh] object-contain rounded-md"
              data-testid="image-preview-img"
            />
          )}
          {caption && (
            <p className="text-center text-white/70 text-sm py-2" data-testid="image-preview-caption">
              {caption}
            </p>
          )}
        </DialogContent>
      </Dialog>
    </ImagePreviewContext.Provider>
  );
}

export const useImagePreview = () => useContext(ImagePreviewContext);

/**
 * Helper component — drop-in clickable image that triggers the lightbox.
 * Usage: <ClickableImage src={url} label="ID Photo" className="w-12 h-12 rounded" />
 *
 * - `fallbackSrc` is shown when src fails to load OR when src is empty (production
 *   migrations may have references to uploaded files that no longer exist).
 * - `fallback` (a React node) overrides everything when no src and no fallbackSrc.
 */
export function ClickableImage({ src, label, className = '', alt, fallback, fallbackSrc, ...rest }) {
  const { open } = useImagePreview();
  const [errored, setErrored] = useState(false);
  // Reset error state when src changes
  useEffect(() => { setErrored(false); }, [src]);

  const effective = (!src || errored) ? fallbackSrc : src;
  if (!effective) return fallback ?? null;
  const clickable = !errored && src;

  return (
    <img
      src={effective}
      alt={alt || label || 'Image'}
      className={`${clickable ? 'cursor-zoom-in hover:opacity-80' : ''} transition-opacity ${className}`}
      onClick={clickable ? (e) => { e.stopPropagation(); open(src, label); } : undefined}
      onError={() => { if (!errored) setErrored(true); }}
      onLoad={(e) => {
        // Some broken file endpoints respond 200 with a 1x1 garbage image instead
        // of a 404. Treat tiny-natural-size results as a load failure too.
        const w = e.currentTarget.naturalWidth;
        const h = e.currentTarget.naturalHeight;
        if ((w <= 2 || h <= 2) && !errored && src && effective === src) {
          setErrored(true);
        }
      }}
      data-testid="clickable-image"
      {...rest}
    />
  );
}

/** Inline SVG data-URI placeholders — no extra network requests, no broken-icon. */
export const ID_PLACEHOLDER = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(
  `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 400 250' preserveAspectRatio='xMidYMid slice'>
    <defs><linearGradient id='g' x1='0' x2='1' y1='0' y2='1'><stop offset='0' stop-color='#0A254D'/><stop offset='1' stop-color='#041A3B'/></linearGradient></defs>
    <rect width='400' height='250' fill='url(#g)'/>
    <rect x='30' y='40' width='110' height='140' rx='6' fill='#1B355C' stroke='#2A4A78' stroke-width='2'/>
    <circle cx='85' cy='95' r='22' fill='#2A4A78'/>
    <path d='M55 145 Q85 120 115 145 L115 165 L55 165 Z' fill='#2A4A78'/>
    <rect x='160' y='60' width='200' height='10' rx='3' fill='#2A4A78'/>
    <rect x='160' y='85' width='160' height='8' rx='3' fill='#1B355C'/>
    <rect x='160' y='105' width='180' height='8' rx='3' fill='#1B355C'/>
    <rect x='160' y='125' width='140' height='8' rx='3' fill='#1B355C'/>
    <text x='200' y='220' fill='#5C7599' font-family='sans-serif' font-size='14' text-anchor='middle'>No ID Uploaded</text>
  </svg>`
);

export const SELFIE_PLACEHOLDER = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(
  `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 300 300' preserveAspectRatio='xMidYMid slice'>
    <defs><linearGradient id='g2' x1='0' x2='1' y1='0' y2='1'><stop offset='0' stop-color='#0A254D'/><stop offset='1' stop-color='#041A3B'/></linearGradient></defs>
    <rect width='300' height='300' fill='url(#g2)'/>
    <circle cx='150' cy='115' r='45' fill='#2A4A78'/>
    <path d='M85 220 Q150 170 215 220 L215 260 L85 260 Z' fill='#2A4A78'/>
    <text x='150' y='285' fill='#5C7599' font-family='sans-serif' font-size='16' text-anchor='middle'>No Selfie</text>
  </svg>`
);
